package com.evoapps.evoshift.domain.usecase.schedule

import com.evoapps.evoshift.domain.repository.AiScheduleRepository
import com.evoapps.evoshift.domain.schedule.SchedulePlan
import com.evoapps.evoshift.domain.schedule.ScheduleRange
import com.evoapps.evoshift.domain.schedule.ai.AiAssignException
import com.evoapps.evoshift.domain.schedule.ai.AiAssignFailure
import com.evoapps.evoshift.domain.schedule.ai.AiAssignStatus
import com.evoapps.evoshift.domain.schedule.ai.AnonymisedScheduleProblem
import kotlin.coroutines.cancellation.CancellationException
import kotlin.random.Random

/**
 * The AI-assisted plan (SH-90): [plan] is always a valid plan; [fallback] says why it is the
 * rules' plan alone (null: the AI's proposals were used where they passed every hard rule).
 */
data class AiAutoSchedule(val plan: SchedulePlan, val fallback: AiAssignFailure?)

/**
 * Auto-assign with AI suggestions (SH-90). Reads the same problem as [PlanAutoScheduleUseCase],
 * pseudonymises it ([AnonymisedScheduleProblem]: aliases, numbers, enums and clock times only),
 * asks the model through [AiScheduleRepository], maps the answer back on the device and lets
 * the rules engine check every proposal against every hard rule before it fills what is left.
 * When the AI is off, over quota or fails, the rules' plan is returned with the reason: the
 * manager always gets a plan.
 */
class SuggestAutoScheduleWithAiUseCase(
    private val planAutoSchedule: PlanAutoScheduleUseCase,
    private val repository: AiScheduleRepository,
    /** Shuffles the aliases; a seeded one in tests. */
    private val random: Random = Random.Default
) {
    /** Whether the server offers AI suggestions at [workplaceId] now. */
    suspend fun status(workplaceId: String): Result<AiAssignStatus> = repository.status(workplaceId)

    suspend operator fun invoke(
        rosterId: String,
        range: ScheduleRange? = null,
        readPayTerms: Boolean = true
    ): Result<AiAutoSchedule> = planAutoSchedule.prepare(rosterId, range, readPayTerms = readPayTerms).mapCatching { prepared ->
        val anonymised = try {
            AnonymisedScheduleProblem.build(prepared.problem, random)
        } catch (e: AiAssignException) {
            return@mapCatching AiAutoSchedule(prepared.plan(), e.failure)
        }
        // Nothing open: nothing to ask (and no request used up).
        if (anonymised.request.shifts.none { it.open }) return@mapCatching AiAutoSchedule(prepared.plan(), null)
        repository.suggest(prepared.workplaceId, anonymised.request).fold(
            onSuccess = { answer ->
                val (proposals, unknown) = anonymised.aliases.resolve(answer)
                val plan = prepared.plan(proposals)
                AiAutoSchedule(plan.copy(dropped = unknown + plan.dropped), null)
            },
            onFailure = { e ->
                if (e is CancellationException) throw e
                AiAutoSchedule(prepared.plan(), (e as? AiAssignException)?.failure ?: AiAssignFailure.UNKNOWN)
            }
        )
    }
}
